import { useState } from 'react';
import { SelectOption } from '@anytag/shared-types';
import { Combobox, useCombobox } from '@anyui/core';
import { useDeepCompareEffect } from '@anytag/shared-hooks';
import { SelectOptionValue } from './types';
import { useMultipleSelectContext } from './select-context';
import { getAllSelectableValues, getOptionValue, getValuesArray } from './utils';
import {
  SelectItem,
  SelectMenu,
  SelectSearch,
  SelectSubMenu,
  SelectTrigger,
  SelectDropdown,
  SelectSubItems,
  SelectAllOption,
  SelectSubTrigger
} from './components';

export const MultiSelect = <TOption extends SelectOption<string>>() => {
  const {
    setSearch,
    comboboxProps,
    selectAllValue,
    hoveringOption,
    setHoveringOption,
    selectProps: { limit, value, onChange, options = [] }
  } = useMultipleSelectContext<TOption>();
  // Selected values will be kept in internal state until dropdown is closed (use string[] internally)
  const [localMultiValue, setLocalMultiValue] = useState<string[]>([]);

  // Use string[] internally for comparisons
  const propValues = getValuesArray({ value, options, multiple: true }) as string[];

  // Sync prop values
  useDeepCompareEffect(() => {
    setLocalMultiValue(propValues);
  }, [propValues]);

  const combobox = useCombobox({
    onDropdownOpen: () => {
      setLocalMultiValue(propValues); // Sync prop value with local multiple value state when dropdown is opened
    },
    onDropdownClose: () => {
      setSearch(''); // Reset search on close
      setHoveringOption(null); // Reset hovering option on close
      // Only trigger onChange if values actually changed
      const propIds = [...propValues].sort().join(',');
      const localIds = [...localMultiValue].sort().join(',');
      if (propIds !== localIds) {
        onChange?.(localMultiValue as SelectOptionValue<TOption>[]);
      }
    }
  });

  // We keep selected values in internal state when dropdown is open, only call onChange when dropdown is closed
  const selectedValues = combobox.dropdownOpened ? localMultiValue : propValues;
  const hasSelectedValue = selectedValues.length > 0;
  const allSelectableValues = getAllSelectableValues(options);
  const hasLimit = limit !== undefined;
  const isLimitReached = hasLimit && selectedValues.length >= limit;
  const isSelectedAll = allSelectableValues.every((v) => selectedValues.includes(v));

  const handleOptionSubmit = (optionValue: string) => {
    if (optionValue === selectAllValue) {
      if (hasLimit) return;
      // toggle select all | unselect all
      setLocalMultiValue(isSelectedAll ? [] : allSelectableValues);
    } else {
      // Update local state only, don't call onChange until dropdown is closed
      const isSelected = localMultiValue.includes(optionValue);
      if (!isSelected && isLimitReached) return; // Only allow unselect if reach the limit

      const newValues = isSelected
        ? localMultiValue.filter((v) => v !== optionValue)
        : [...localMultiValue, optionValue];
      setLocalMultiValue(newValues);
    }
  };

  // Handle clicking on parent option with submenu (select/deselect all submenu items)
  const handleSubmenuGroupClick = (submenuItems: SelectOption<string>[], allSelected: boolean) => {
    const submenuValues = submenuItems.map((s) => getOptionValue(s));
    if (allSelected) {
      // Deselect all submenu items
      setLocalMultiValue(localMultiValue.filter((v) => !submenuValues.includes(v)));
    } else {
      // Select all submenu items
      const newValues = [...new Set([...localMultiValue, ...submenuValues])];
      if (hasLimit && newValues.length > limit) return; // Only allow select all if new values not reach the limit

      setLocalMultiValue(newValues);
    }
  };

  return (
    <Combobox store={combobox} onOptionSubmit={handleOptionSubmit} {...comboboxProps}>
      <SelectTrigger selectedValues={selectedValues} onClear={() => onChange?.([] as SelectOptionValue<TOption>[])} />

      <SelectDropdown>
        <SelectSearch selectedValues={selectedValues} />
        <SelectMenu>
          <SelectAllOption checked={hasSelectedValue} indeterminate={!isSelectedAll} />

          {options.map((option) => {
            const optValue = getOptionValue(option);
            const hasSubmenu = option.submenu && option.submenu.length > 0;

            if (hasSubmenu) {
              const submenuValues = option.submenu?.map((s) => getOptionValue(s)) ?? [];
              const hasAllSubmenuSelected = !!hasSubmenu && submenuValues.every((v) => selectedValues.includes(v));
              const hasSomeSubmenuSelected = !!hasSubmenu && submenuValues.some((v) => selectedValues.includes(v));

              return (
                <SelectSubMenu key={optValue} open={hoveringOption === optValue}>
                  <SelectSubTrigger
                    option={option}
                    active={hasSomeSubmenuSelected}
                    indeterminate={!hasAllSubmenuSelected}
                    onClick={() => handleSubmenuGroupClick(option.submenu!, hasAllSubmenuSelected)}
                  />
                  <SelectSubItems
                    option={option}
                    disabled={isLimitReached}
                    onSelect={handleOptionSubmit}
                    selectedValues={selectedValues}
                  />
                </SelectSubMenu>
              );
            }

            return (
              <SelectItem
                key={optValue}
                option={option}
                active={selectedValues.includes(optValue)}
                disabled={isLimitReached && !selectedValues.includes(optValue)}
              />
            );
          })}
        </SelectMenu>
      </SelectDropdown>
    </Combobox>
  );
};
